import {
    MILITARY_VARIANT,
    STC_VARIANT,
    collection,
    isMilitaryEnabled,
    parentTypeFor,
} from "../_variants/index.js";
import {
    addDOI,
    cancelDOI,
    checkDOI,
    editDOI,
    linkDOI,
    reviseDOI
} from "./actions";

import { get } from "lodash";
import store from "../../../store";
import { canRevise } from "./canRevise.js";

function warning(key) {
    let item = store.state[key];
    if (!item || item.Cancelled === true) return false;
    let warningMessage = "";
    if (canRevise(item) === false) {
        warningMessage = warningMessage += `${item.Number} is not approved.<br>`;
    }
    return warningMessage === "" ? false : warningMessage;
}

const cellClassRules = {
    "text-orange font-bold": params => {
        return warning(params.data);
    }
}

const headers = [{
    "headerName": "Number",
    "field": "Number",
    cellClassRules
}, {
    "headerName": "Revision",
    "field": "Revision",
    cellClassRules
}, {
    "headerName": "Comment",
    "field": "Comment",
    cellClassRules
}, {
    "headerName": "Compiler",
    "field": "Signedoff",
    "type": "bool",
    cellClassRules,
    valueFormatter(params) {
        return get(params.data, `Revisions.${params.data.RevisionID}.Quality.Compiler.Completed`);
    }
}, {
    "headerName": "Checker",
    "field": "Signedoff",
    "type": "bool",
    cellClassRules,
    valueFormatter(params) {
        return get(params.data, `Revisions.${params.data.RevisionID}.Quality.Checker.Completed`);
    }
}, {
    "headerName": "Approver",
    "field": "Signedoff",
    "type": "bool",
    cellClassRules,
    valueFormatter(params) {
        return get(params.data, `Revisions.${params.data.RevisionID}.Quality.Approver.Completed`);
    }
}];

const ADDABLE_PARENT_TYPES = ["DesignChanges", "FlightConditions", "MilitaryDesignChanges", "MilitaryFlightConditions"];

/**
 * Build the attachable-table descriptor for the DOI register that matches
 * the supplied variant.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeDOIAttachable = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military DOI" : "DOI";
    const labelPlural = isMilitary ? "Military DOIs" : "DOIs";
    const doiParentType = parentTypeFor(variant, "DOIs");
    const doiCollection = collection(variant, "DOIs");

    const actions = [{
        "label": `Add ${labelSingular}`,
        "action": addDOI,
        show({ meta }) {
            return [ADDABLE_PARENT_TYPES.includes(meta?.ParentType), `${labelPlural} cannot be added to a Document, they must be linked.`]
        }
    }, {
        "label": `Link ${labelSingular}`,
        "action": linkDOI
    }];

    const rowActions = [{
        "label": `View ${labelSingular}`,
        action({ router, rowItem }) {
            router.push(`/Database/${doiParentType}/${rowItem.ID}`)
        }
    }, {
        "label": `Update ${labelSingular}`,
        "break": true,
        "action": editDOI
    }, {
        "label": `Revise ${labelSingular}`,
        "action": reviseDOI,
        show({ rowItem }) {
            return [canRevise(rowItem), "The DOI is not approved"]
        }
    }, {
        "label": "Compiler Check",
        "action": checkDOI("Compiler"),
        "break": true,
        show({ rowItem }) {
            return rowItem.Revisions[rowItem.RevisionID]?.Quality?.Compiler?.Completed === true ? [false, "Already Complier Checked"] : [true, ""];
        }
    },
    {
        "label": "Checker Check",
        "action": checkDOI("Checker"),
        show({ rowItem }) {
            return rowItem.Revisions[rowItem.RevisionID]?.Quality?.Checker?.Completed === true ?
                [false, "Already Complier Checked"] :
                !rowItem.Revisions[rowItem.RevisionID]?.Quality?.Compiler?.Completed ?
                    [false, "DOI not checked by the complier"] :
                    [true, ""]
        }
    },
    {
        "label": "Approver Check",
        "action": checkDOI("Approver"),
        show({ rowItem }) {
            return rowItem.Revisions[rowItem.RevisionID]?.Quality?.Approver?.Completed === true ?
                [false, "Already Approver Checked"] :
                !rowItem.Revisions[rowItem.RevisionID]?.Quality?.Checker?.Completed ?
                    [false, "DOI not checked by the checker"] :
                    [true, ""];
        }
    }, {
        "label": `Cancel ${labelSingular}`,
        "break": true,
        "action": cancelDOI,
        "class": "text-red"
    }];

    return {
        "label": labelPlural,
        "key": doiParentType,
        "location": `${doiCollection}/`,
        actions,
        rowActions,
        headers,
        warn({ items }) {
            let warningMessage = items.map(warning).filter(i => i !== false).join("");
            return [warningMessage !== "", warningMessage];
        }
    };
};

export const doiAttachable = makeDOIAttachable(STC_VARIANT);
export const militaryDOIAttachable = isMilitaryEnabled() ? makeDOIAttachable(MILITARY_VARIANT) : null;

export default doiAttachable;
